import { Helmet } from "react-helmet-async";
import LegalLayout from "@/components/legal/LegalLayout";
import { Link } from "react-router-dom";

const sections = [
  { id: "purpose", title: "Purpose" },
  { id: "sources", title: "Data Sources & Cadence" },
  { id: "coverage", title: "Coverage Boundaries" },
  { id: "classification", title: "Classification & Enrichment" },
  { id: "limitations", title: "Known Limitations" },
  { id: "citation", title: "Citation Standard" },
  { id: "corrections", title: "Corrections Process" },
  { id: "contact", title: "Contact" },
];

const Methodology = () => (
  <>
    <Helmet>
      <title>Methodology — The Emerging Market</title>
      <meta
        name="description"
        content="How The Emerging Market collects, classifies, and refreshes Alabama civic and funding data — sources, cadence, coverage boundaries, and correction process."
      />
      <link rel="canonical" href="https://emrkt.co/methodology" />
      <meta property="og:title" content="Methodology — The Emerging Market" />
      <meta property="og:url" content="https://emrkt.co/methodology" />
      <meta property="og:type" content="website" />
    </Helmet>
    <LegalLayout
      title="Methodology"
      description="What our data actually says, where it comes from, how often it refreshes, and what it does not cover."
      lastUpdated="July 4, 2026"
      sections={sections}
    >
      <section id="purpose">
        <h2>1. Purpose</h2>
        <p>
          The Emerging Market publishes structured intelligence on Alabama's
          emerging economic ecosystem: state and federal legislation touching
          Alabama, grant programs available to Alabama-based organizations,
          IRS Form 990 grantmakers with Alabama exposure, and community-sourced
          project ideas. This page documents how that intelligence is built so
          institutional users (funders, agencies, journalists, researchers) can
          weigh confidence and cite responsibly.
        </p>
      </section>

      <section id="sources">
        <h2>2. Data Sources & Cadence</h2>
        <h3>Legislation</h3>
        <ul>
          <li>
            <strong>Alabama state bills</strong> — LegiScan <code>getMasterList</code>
            {" "}+ <code>getBill</code>, refreshed daily at 07:00 UTC. Up to 200 bills
            ingested per run to stay well within the free-tier query budget.
          </li>
          <li>
            <strong>Federal bills</strong> — Congress.gov{" "}
            <code>/member/{"{bioguide}"}/sponsored-legislation</code> for every
            member of the Alabama congressional delegation. Refreshed daily at
            07:15 UTC.
          </li>
        </ul>
        <h3>Grants & funders</h3>
        <ul>
          <li>
            <strong>Curated grant programs</strong> — hand-curated{" "}
            <code>grant_programs</code> table, verified by editorial before
            publish. See our <Link to="/data-tools">/data-tools</Link> for
            what's live today.
          </li>
          <li>
            <strong>Live AI grant search</strong> — <code>find-grants</code>
            {" "}edge function runs a fresh search per request through the Lovable
            AI Gateway. Not cached; results are advisory.
          </li>
          <li>
            <strong>IRS Form 990 grantmakers</strong> — ProPublica Nonprofit
            Explorer, ingested into <code>funder_990_profiles</code> +{" "}
            <code>funder_990_grants</code>. Scope: funders headquartered in
            Alabama; grants filtered to Alabama recipients.
          </li>
        </ul>
        <h3>Community signal</h3>
        <ul>
          <li>
            <strong>Ideas &amp; projects</strong> — user-submitted, published
            under the platform's editorial and moderation policies.
          </li>
          <li>
            <strong>News annotations</strong> — Google News RSS filtered for
            Alabama legislation coverage, with a hard-coded fallback list when
            the feed is unreachable.
          </li>
        </ul>
        <p>
          Every ingest run writes to the internal <code>ingest_runs</code>{" "}
          table (items seen / inserted / updated / failed, run duration, error
          string on failure). An <code>ingest-monitor</code> job runs every 6
          hours and alerts on any source that has been stale for more than 36
          hours.
        </p>
      </section>

      <section id="coverage">
        <h2>3. Coverage Boundaries</h2>
        <p>
          The platform is explicitly Alabama-scoped. Where we ingest data with
          broader geography (e.g. federal bills, ProPublica), we filter to
          Alabama exposure before storing.
        </p>
        <ul>
          <li>Ideas, projects, and partner network: all 67 Alabama counties, with a Mobile County pilot depth.</li>
          <li>State legislation: full current session, refreshed daily.</li>
          <li>Federal legislation: sponsorships by the Alabama delegation only. Bills the delegation opposes or does not sponsor are out of scope.</li>
          <li>Grants: Alabama-eligible programs. National programs are listed when Alabama organizations are eligible applicants.</li>
          <li>990 data: funders headquartered in Alabama, plus their national grant activity.</li>
        </ul>
      </section>

      <section id="classification">
        <h2>4. Classification & Enrichment</h2>
        <p>
          Bill and grant text is classified into focus areas — Health &amp;
          Wellness, Public Safety, Education, Infrastructure, Housing,
          Economic Development, Agriculture, Environment — using a keyword
          rule set. First match wins; unmatched text falls back to{" "}
          <em>General</em>. The rules are conservative and rule-based (not ML):
          reviewable, auditable, and predictable across ingest runs.
        </p>
        <p>
          Every ingested bill carries the raw upstream payload{" "}
          (<code>source_payload</code>), a stable content hash of the fields
          that trigger a revision (title + status + last action + sponsors +
          description), and a source URL. When any of those fields change, a
          new row lands in <code>legislation_revisions</code> for audit and
          alerting.
        </p>
      </section>

      <section id="limitations">
        <h2>5. Known Limitations</h2>
        <ul>
          <li>
            <strong>Statutes and regulations.</strong> The pipeline covers
            active bills only. Alabama's ALISON statute corpus and the federal
            govinfo regulations catalog are on the roadmap.
          </li>
          <li>
            <strong>County attribution.</strong> Bills are not tagged to a
            county unless the upstream source provides one. County filtering
            on the tracker is therefore best-effort.
          </li>
          <li>
            <strong>Full-text search &amp; embeddings.</strong> Search is
            keyword-based today. Semantic / embedding-based search will be
            added once the ingested corpus is large enough to justify it.
          </li>
          <li>
            <strong>Live AI grant search.</strong> Not cached, not deduplicated
            across sessions. Treat individual results as advisory pending human
            verification.
          </li>
          <li>
            <strong>990 grant text.</strong> Purpose fields on Form 990
            grantmaker filings are free text; program classification is
            heuristic and may misgroup edge cases.
          </li>
          <li>
            <strong>Data freshness.</strong> Bills and grants refresh daily.
            An outage in an upstream source will surface as a stale
            <code>ingest_runs</code> row and an admin alert, but the
            platform continues to serve the previous snapshot in the
            meantime.
          </li>
        </ul>
      </section>

      <section id="citation">
        <h2>6. Citation Standard</h2>
        <p>
          Every ingested record links back to its upstream source. When you
          cite our data, cite the underlying source (LegiScan bill URL,
          Congress.gov URL, ProPublica page) and note the retrieval date; The
          Emerging Market is the aggregator, not the primary source. Detailed
          citation guidance is in our internal editorial docs and mirrors the
          practice we ask contributors to follow.
        </p>
      </section>

      <section id="corrections">
        <h2>7. Corrections Process</h2>
        <p>
          If a bill status, sponsor count, grant deadline, funder record, or
          classification looks wrong, email{" "}
          <a href="mailto:info@emrkt.co">info@emrkt.co</a> with the URL and a
          short description. We aim to acknowledge within 3 business days and
          publish a correction (or an explanation) within 10. Content-hash
          revisions are audit-logged, so we can show precisely when a record
          changed and why.
        </p>
      </section>

      <section id="contact">
        <h2>8. Contact</h2>
        <p>
          Methodology questions, license inquiries, and press requests:{" "}
          <a href="mailto:info@emrkt.co">info@emrkt.co</a>. For institutional
          use, see also{" "}
          <Link to="/for-funders">/for-funders</Link> and{" "}
          <Link to="/for-agencies">/for-agencies</Link>.
        </p>
      </section>
    </LegalLayout>
  </>
);

export default Methodology;